'use client';

import { analiticaInstance } from '@payments-checkout/shared/plugins/analitica/analitica.events';
import { DigitalWalletsType } from '@payments-checkout/shared/plugins/analitica/analitica.types';
import {
  usePaymentMethod,
  usePaymentResponse,
} from '@payments-checkout/shared/providers';
import { Alert, Button, Divider, Icon, Text } from '@stone-payments/jade';
import Link from 'next/link';
import { useEffect } from 'react';
import { useIntl } from 'react-intl';
import { Replace } from '../../app/components/replace';
import { usePageSharedData } from '../../hooks/usePageSharedData';
import { Success } from '../paymentStatus/paymentStatus';

type DeliveryContentProps = {
  isPending: boolean;
  isDeliverySuccess: boolean | undefined;
  trackingUrl: string | undefined;
  trackingCode: string | undefined;
};

function DeliveryContent({ isPending, isDeliverySuccess, trackingUrl, trackingCode }: DeliveryContentProps) {
  const { formatMessage } = useIntl();

  if (!isPending && isDeliverySuccess) {
    return (
      <>
        <Text color="medium" variant="text-large" weight="medium" className="text-left">
          {formatMessage(
            { id: 'payment.status.card.tracking.message' },
            {
              trackingCode: (
                <a
                  href={trackingUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{ color: 'inherit', textDecoration: 'underline' }}
                >
                  {trackingCode}
                </a>
              ),
            }
          )}
        </Text>
        <div className="w-full pt-jade-200">
          <Button
            className="w-full"
            size="medium"
            variant="primary-solid"
            onClick={() => {
              analiticaInstance.events.deliveryEvents.trackingButtonClicked();
              if (trackingUrl) window.open(trackingUrl, '_blank', 'noopener,noreferrer');
            }}
          >
            {formatMessage({ id: 'payment.status.card.tracking.button' })}
          </Button>
        </div>
      </>
    );
  }

  if (isPending) {
    return (
      <Text color="medium" variant="text-large" weight="medium" className="text-left">
        {formatMessage({ id: 'payment.status.card.tracking.message.pending' })}
      </Text>
    );
  }

  return (
    <Text color="medium" variant="text-large" weight="medium" className="text-left">
      {formatMessage({ id: 'payment.status.card.tracking.failed' })}
    </Text>
  );
}

function DeliverySection({ isPending, isDeliverySuccess, trackingUrl, trackingCode }: DeliveryContentProps) {
  const { formatMessage } = useIntl();

  return (
    <div>
      <div className="block w-full py-2">
        <Divider direction="horizontal" size="small" />
      </div>
      <Text color="current-color" variant="heading-xsmall" className="text-left">
        {formatMessage({ id: 'payment.status.card.tracking.title' })}
      </Text>
      <DeliveryContent
        isPending={isPending}
        isDeliverySuccess={isDeliverySuccess}
        trackingUrl={trackingUrl}
        trackingCode={trackingCode}
      />
    </div>
  );
}

export function PaymentStatusCard() {
  const { formatMessage } = useIntl();
  const { paymentMethod, selectedDigitalWallet } = usePaymentMethod();
  const { paymentResponse } = usePaymentResponse();
  const { paymentLinkIdData, hasInvalidPaymentSelected, showAddressPage, isDeliveryEnabled } =
    usePageSharedData();

  const isPending = paymentResponse?.order_status === 'pending';
  const isDeliverySuccess = paymentResponse?.delivery?.success;
  const successUrl = paymentLinkIdData?.data?.checkout_settings?.success_url;

  const title = isPending
    ? formatMessage({ id: 'payment.status.card.pending' })
    : formatMessage({ id: 'payment.status.card.success' });

  const successSubtitle = showAddressPage
      ? formatMessage({ id: 'payment.status.card.success.description.delivery' })
      : formatMessage({ id: 'payment.status.card.success.description' });

  const subtitle = isPending
    ? formatMessage({ id: 'payment.status.card.pending.description' })
    : successSubtitle;

  const icon = isPending
    ? <Icon use="circle-clock" color="medium" size="xlarge" />
    : <Icon use="circle-check" color="positive" size="xlarge" />;

  useEffect(() => {
    if (!paymentResponse) return;
    if (paymentMethod === 'credit_card') {
      analiticaInstance.events.creditCardPage.successCreditCardViewed();
    } else if (selectedDigitalWallet) {
      const digitalWalletPossibleValues = {
        applePay: 'Apple Pay',
        googlePay: 'Google Pay',
        clickToPay: 'Click to pay',
      };
      analiticaInstance.events.creditCardPage.successDigitalWalletsViewed(
        digitalWalletPossibleValues[selectedDigitalWallet] as DigitalWalletsType
      );
    }
  }, []);

  if (hasInvalidPaymentSelected) return <Replace />;

  return (
    <Success icon={icon} title={title} subtitle={subtitle}>
      {isPending && (
        <Alert
          description={formatMessage({ id: 'payment.status.card.pending.info' })}
          showIcon
          title={formatMessage({ id: 'payment.status.card.pending.info.title' })}
          variant="info"
        />
      )}
      {successUrl && (
        <Link href={successUrl} target="_blank" className="w-full flex no-underline mt-jade-400">
          <Button className="mt-jade-400 w-full" size="medium">
            {formatMessage({ id: 'paymentSuccess.backToStore' })}
          </Button>
        </Link>
      )}
      {isDeliveryEnabled && (
        <DeliverySection
          isPending={isPending}
          isDeliverySuccess={isDeliverySuccess}
          trackingUrl={paymentResponse?.delivery?.tracking_url}
          trackingCode={paymentResponse?.delivery?.tracking_code}
        />
      )}
    </Success>
  );
}
